/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
import React from 'react';
import { Typography, Box, Grid } from '@mui/material';
import { alpha } from '@mui/material/styles';
import modifyScriptName from '../../../../utils/modifyScriptName';
import { fDateTime } from '../../../../utils/formatTime';
import { fNumber } from '../../../../utils/formatNumber';

const TradesRow = ({ trade, showTime = false }) => {
  const { type, buy, sell, boughtAt, soldAt, script, profit, brokerage, side, updatedAt, boughtBy, soldBy } = trade;

  let color;
  if (['EXECUTED', 'CANCELLED'].includes(type)) {
    color = 'green';
  }
  if (['OPEN', 'ORDER', 'PENDING'].includes(type)) {
    if (side === 'BUY') {
      color = 'blue';
    } else {
      color = 'red';
    }
  } return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: (theme) => theme.palette.mode === 'light' ? 'grey.200' : 'grey.800',
        borderRadius: 1,
        width: '100%',
        cursor: 'pointer',
        transition: 'all 0.15s ease-in-out',
        bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
        ':hover': {
          borderColor: (theme) => theme.palette.mode === 'light' ? 'primary.main' : 'primary.light',
          bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.100' : 'grey.800',
          transform: 'translateX(2px)',
        }
      }}
    >
      <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {/* Top Row */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 2,
            mb: 0.75
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                }}
              >
                {modifyScriptName(script.tradingsymbol, script.expiry)}
              </Typography>
              <Box
                sx={{
                  px: 1,
                  height: '24px',
                  borderRadius: 1,
                  fontSize: '0.813rem',
                  fontWeight: 600,
                  lineHeight: 1,
                  textTransform: 'uppercase',
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: side === 'BUY' ? 'success.main' : 'error.main',
                  bgcolor: side === 'BUY' ? 'success.lighter' : 'error.lighter',
                  flexShrink: 0,
                  letterSpacing: '0.02em',
                }}
              >
                {type === 'ORDER' && `${showTime ? `CLOSE ${side === 'BUY' ? 'SELL ' : 'BUY '}` : ''}`}
                {type === 'OPEN' && `NEW ${side}`}
                {['EXECUTED', 'CANCELLED'].includes(type) && `${type} ${side} `}
                {trade.quantity}
              </Box>
            </Box>
            {type === 'ORDER' && (
              <Box
                sx={{
                  flexShrink: 0,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: (theme) => profit > 0
                    ? alpha(theme.palette.success.main, 0.08)
                    : alpha(theme.palette.error.main, 0.08)
                }}
              >
                <Typography
                  sx={{
                    color: profit > 0 ? 'success.main' : 'error.main',
                    fontWeight: 700,
                    fontSize: '1rem',
                    lineHeight: 1,
                    letterSpacing: '0.02em',
                  }}
                >
                  {fNumber(profit?.toFixed(2) || 0)}
                </Typography>
              </Box>
            )}
          </Box>          {/* Bottom Row */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 } }}>
            {type === 'ORDER' && showTime && (
              <Grid container spacing={1} sx={{ fontSize: '0.75rem' }}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>BOUGHT:</Typography>
                    <Typography variant="caption">{boughtAt ? fDateTime(boughtAt) : '-'} ({boughtBy})</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>SOLD:</Typography>
                    <Typography variant="caption">{soldAt ? fDateTime(soldAt) : '-'} ({soldBy})</Typography>
                  </Box>              </Grid>
              </Grid>
            )}

            {['EXECUTED', 'CANCELLED'].includes(type) && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  {type === 'CANCELLED' ? 'CANCELLED:' : 'UPDATED:'}
                </Typography>
                <Typography variant="caption">{fDateTime(updatedAt)}</Typography>
              </Box>
            )}

            {type === 'OPEN' && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  {side === 'BUY' ? 'BOUGHT:' : 'SOLD:'}
                </Typography>
                <Typography variant="caption">{fDateTime(side === 'BUY' ? boughtAt : soldAt)}</Typography>
              </Box>
            )}
            {/* Price Details */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: { xs: 1, sm: 1.5 },
              color: 'text.secondary',
              fontSize: '0.875rem',
              fontWeight: 500,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              flexWrap: { xs: 'wrap', sm: 'nowrap' }
            }}>
              {buy && (
                <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: 'inherit',
                      color: 'text.secondary',
                      fontWeight: 600
                    }}
                  >
                    BUY:
                  </Typography>
                  <Typography sx={{ fontSize: 'inherit', fontWeight: 500 }}>
                    {fNumber(buy?.toFixed(2)) || '--'}
                  </Typography>
                </Box>
              )}
              {sell && (
                <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: 'inherit',
                      color: 'text.secondary',
                      fontWeight: 600
                    }}
                  >
                    SELL:
                  </Typography>
                  <Typography sx={{ fontSize: 'inherit', fontWeight: 500 }}>
                    {fNumber(sell?.toFixed(2)) || '--'}
                  </Typography>
                </Box>
              )}
              {['OPEN', 'ORDER'].includes(trade.type) && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    gap: 0.5,
                    alignItems: 'center',
                    ml: 'auto'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 'inherit',
                      color: 'text.secondary',
                      fontWeight: 600
                    }}
                  >
                    BROKERAGE:
                  </Typography>
                  <Typography sx={{ fontSize: 'inherit', fontWeight: 500 }}>
                    {fNumber(brokerage?.toFixed(2)) || '--'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TradesRow;
